import { ComprehensiveAnalysisResult } from './analysis-results.types';

export enum AnalysisStatus {
  QUEUED = 'queued',
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  FAILED = 'failed',
}

export interface CallInfo {
  agent_id: string;
  contact_id: string;
  duration_ms: number | null;
  duration_seconds: number | null;
}

export interface AudioInfo {
  url: string | null;
  size: number | null;
  type: string | null;
}

export interface TranscriptSegment {
  text: string;
  start?: number;
  end?: number;
  speaker?: string;
}

export interface TranscriptInfo {
  text: string;
  segments?: TranscriptSegment[];
  language?: string;
  duration?: number;
  /** Speaker label (e.g., 'A' or 'B') identified as the agent */
  agent_speaker?: string;
}

export interface MetadataInfo {
  error_message: string | null;
  webhook_url: string | null;
  // ISO 8601 timestamp string. The sibling `created` field is a Unix
  // epoch in seconds (FastAPI's int field), but `completed_at` is a
  // Pydantic datetime that serializes as a string — they are NOT the
  // same shape. Pass directly to `new Date(...)`; do not multiply.
  completed_at: string | null;
}

export interface AnalysisListItem {
  id: string;
  object: string;
  created: number;
  status: AnalysisStatus;
  livemode: boolean;
  call: CallInfo;
  audio: AudioInfo;
  transcript: TranscriptInfo | null;
  results: ComprehensiveAnalysisResult | null;
  metadata: MetadataInfo;
}

export interface AnalysisDetail extends AnalysisListItem {
  // Same as list item, but could be extended with more details
}

export interface SearchMetadata {
  query: string | null;
  filters_applied: string[];
  search_time_ms: number;
}

export interface AnalysisListResponse {
  object: string;
  data: AnalysisListItem[];
  has_more: boolean;
  total: number | null;  // null when filters are applied (skip-COUNT optimization)
  search_metadata?: SearchMetadata | null;
}

export interface AnalysisSearchFilters {
  // Full-text search
  q?: string;
  // Compliance filters
  complianceIdentityVerified?: boolean;
  complianceFlagged?: boolean;
  complianceProhibitedPhrasesUsed?: boolean;
  complianceSensitiveDataShared?: boolean;
  complianceMandatoryDisclosuresMissing?: boolean;
  compliancePurposeNotDeclared?: boolean;
  // Quality filters
  qualitySentimentFlagged?: boolean;
  qualityInterruptionsMin?: number;
  qualityGreetingMissing?: boolean;
  qualityObjectionsUnhandled?: boolean;
  // Coaching filters
  coachingPriority?: 'high' | 'medium' | 'low';
  coachingEffortScoreMin?: number;
  // Date range
  dateFrom?: string;
  dateTo?: string;
}

export interface AnalysisListParams extends AnalysisSearchFilters {
  status?: AnalysisStatus;
  agentId?: string;
  contactId?: string;
  search?: string;
  limit?: number;
  offset?: number;
}
